import { useEffect, useRef, useState } from 'react';

import { Combobox, Pill, PillsInput, useCombobox } from '@mantine/core';

import { useAuthContext, useTags } from '../hooks';

interface PillInputProps {
  tags: string[];
  onAddTag: (tagName: string) => void;
  onRemoveTag: (tagName: string) => void;
  placeholder?: string;
}

export function PillInput({
  tags,
  onAddTag,
  onRemoveTag,
  placeholder = 'Add tag...',
}: PillInputProps) {
  const [search, setSearch] = useState('');
  const { token, logout } = useAuthContext();
  const hasFetchedRef = useRef(false);
  const {
    tags: availableTags,
    loading: tagsLoading,
    fetchTags,
  } = useTags(
    {
      token: token || undefined,
      onAuthError: logout,
    },
    { pageSize: 150, autoFetch: false },
  );

  useEffect(() => {
    if (token && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchTags();
    }
  }, [token]); // fetchTags intentionally omitted to prevent loop

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
  });

  // Get all available tag names that aren't already selected
  const availableTagNames = availableTags
    .map((tag) => tag.name)
    .filter((tagName) => !tags.includes(tagName));

  const handleValueSelect = (val: string) => {
    if (!tags.includes(val)) {
      onAddTag(val);
    }
    setSearch('');
  };

  const handleValueRemove = (val: string) => {
    onRemoveTag(val);
  };

  const filteredOptions = availableTagNames.filter((item) =>
    item.toLowerCase().includes(search.toLowerCase().trim()),
  );

  const options = filteredOptions.map((item) => (
    <Combobox.Option value={item} key={item}>
      {item}
    </Combobox.Option>
  ));

  return (
    <Combobox store={combobox} onOptionSubmit={handleValueSelect}>
      <Combobox.DropdownTarget>
        <PillsInput onClick={() => combobox.openDropdown()}>
          <Pill.Group>
            {tags.map((tag) => (
              <Pill
                key={tag}
                withRemoveButton
                onRemove={() => handleValueRemove(tag)}
              >
                {tag}
              </Pill>
            ))}

            <Combobox.EventsTarget>
              <PillsInput.Field
                onFocus={() => combobox.openDropdown()}
                onBlur={() => combobox.closeDropdown()}
                value={search}
                placeholder={tags.length === 0 ? placeholder : ''}
                onChange={(event) => {
                  combobox.updateSelectedOptionIndex();
                  setSearch(event.currentTarget.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Backspace' && search.length === 0) {
                    event.preventDefault();
                    if (tags.length > 0) {
                      handleValueRemove(tags[tags.length - 1]);
                    }
                  }
                }}
              />
            </Combobox.EventsTarget>
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>

      <Combobox.Dropdown>
        <Combobox.Options>
          {options.length > 0 ? (
            options
          ) : (
            <Combobox.Empty>
              {tagsLoading ? 'Loading...' : 'No tags available'}
            </Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

export default PillInput;
